import { Check } from 'lucide-react';
import { T, FONT, SERIF } from '../theme';
import { Eyebrow } from '../primitives';
import type { CloseVM } from '../../formatters/deckContent';

interface Props {
  vm: CloseVM;
  onRestart: () => void;
  interactive?: boolean;
}

/** Act 7 — Close: commitments recap + restart link + prepared-by line. */
export function ActClose({ vm, onRestart, interactive = true }: Props) {
  return (
    <div style={{ height: '100%', overflowY: 'auto', background: T.bg }}>
      <div
        style={{
          maxWidth: 1080,
          margin: '0 auto',
          padding: '70px 40px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          minHeight: '100%',
          justifyContent: 'center',
        }}
      >
        <Eyebrow>A plan for the rest of your life</Eyebrow>
        <h1
          style={{
            fontFamily: SERIF,
            fontSize: 40,
            fontWeight: 600,
            letterSpacing: '-0.01em',
            color: T.navy,
            margin: '4px 0 14px',
            lineHeight: 1.1,
            maxWidth: 700,
          }}
        >
          Your future is secure, and your legacy is thriving.
        </h1>
        <p style={{ fontSize: 16, color: T.gray, lineHeight: 1.6, maxWidth: 580, marginBottom: 40 }}>
          Your commitments, now backed by a plan built to deliver them with confidence.
        </p>

        {vm.commitments.length > 0 && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${Math.min(3, vm.commitments.length)},1fr)`,
              gap: 16,
              width: '100%',
              maxWidth: 760,
              marginBottom: 44,
            }}
          >
            {vm.commitments.map((c, i) => (
              <div
                key={i}
                style={{
                  background: T.card,
                  border: `1px solid ${T.grayLine}`,
                  borderRadius: 12,
                  padding: '22px 18px',
                  boxShadow: '0 2px 8px rgba(27,42,74,.05)',
                }}
              >
                <div
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 17,
                    background: T.teal,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 14px',
                  }}
                >
                  <Check size={18} color="#fff" />
                </div>
                <div style={{ fontSize: 28, fontWeight: 700, color: T.navy, lineHeight: 1 }}>{c.value}</div>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: T.teal, marginTop: 6 }}>{c.title}</div>
                <div style={{ fontSize: 12, color: T.gray, marginTop: 2 }}>{c.sub}</div>
              </div>
            ))}
          </div>
        )}

        {/* utility navigation — the CTAs live in the persistent footer */}
        {interactive && (
          <div style={{ display: 'flex', gap: 20, marginTop: 6 }}>
            <button
              onClick={onRestart}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontFamily: FONT,
                fontSize: 13,
                color: T.gray,
                textDecoration: 'underline',
                textUnderlineOffset: 3,
              }}
            >
              Review the plan from the start
            </button>
          </div>
        )}

        {vm.preparedByLine && <div style={{ marginTop: 44, fontSize: 12.5, color: T.gray }}>{vm.preparedByLine}</div>}
      </div>
    </div>
  );
}
